import React, { useEffect, useState } from 'react';
import {
  GalleryHorizontal, LayoutGrid, Plus, Edit3, Trash2, X, Loader2,
  Image as ImgIcon, Eye, EyeOff, Upload,
 HelpCircle,} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../lib/api';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../lib/mediaAsset';
import { useToast } from '../hooks/use-toast';
import HomeCategoryCards from './HomeCategoryCards';
import HomeFaqEditor from './HomeFaqEditor';

// Sitedeki ana slider bu oranla çizilir. Panelde başka bir oranla önizlenirse
// yönetici "panelde düzgündü" diye yükler, site kenarlardan kırpar — bu yüzden
// önizleme, önerilen ölçü ve sitenin CSS'i aynı sayıya bakar.
const SLIDE_RATIO = '8 / 3';
const SLIDE_SIZE_HINT = 'Önerilen ölçü 1600×600 (8:3)';

const emptySlide = {
  placement: 'main',
  title: '',
  subtitle: '',
  highlight: '',
  description: '',
  cta_text: 'Detaylar',
  cta_link: '',
  image: '',
  order: 0,
  is_active: true,
};

// Anahtarlar backend'deki ``models.SliderPlacement`` kümesiyle **aynı
// kalmalıdır**: listede olmayan bir değer kaydedilirken 422 döner.
const placementLabels = {
  main: 'Ana Slider',
  side: 'Yan Banner',
  slot: 'Slot Sayfası',
  'live-casino': 'Canlı Casino',
};

const Sliders = () => {
  const [tab, setTab] = useState('banners');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptySlide);
  const [busy, setBusy] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  // Yüklenen görselin en/boy oranı; slider oranından belirgin saparsa yönetici
  // uyarılır, yoksa görsel sitede sessizce kenarlarından kırpılır.
  const [imageRatio, setImageRatio] = useState(null);
  const { toast } = useToast();

  const targetRatio = 8 / 3;
  const ratioWarning = imageRatio && Math.abs(imageRatio - targetRatio) / targetRatio > 0.12
    ? `Bu görselin oranı ${imageRatio.toFixed(2)}:1 — slider ${targetRatio.toFixed(2)}:1 çizer, kenarlardan kırpılır. ${SLIDE_SIZE_HINT}.`
    : '';

  const load = async () => {
    setLoading(true);
    try { const { data } = await client.get('/admin/sliders'); setItems(data.items || []); } catch {}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptySlide); setImageRatio(null); setModal({ mode: 'new' }); };
  const openEdit = (s) => { setForm({ ...emptySlide, ...s }); setImageRatio(null); setModal({ mode: 'edit', id: s.id }); };
  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({ title: 'Görsel yüklenemedi', description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.` });
      return;
    }

    setUploadingImage(true);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await client.post('/admin/uploads/images', body);
      setForm((current) => ({ ...current, image: data.url }));
      toast({ title: 'Banner görseli yüklendi' });
    } catch (error) {
      toast({
        title: 'Görsel yüklenemedi',
        description: error.response?.data?.detail || 'Dosya yükleme başarısız.',
      });
    } finally {
      setUploadingImage(false);
    }
  };
  const save = async () => {
    setBusy(true);
    try {
      if (modal.mode === 'new') await client.post('/admin/sliders', form);
      else await client.patch(`/admin/sliders/${modal.id}`, form);
      toast({ title: 'Kaydedildi' });
      setModal(null); load();
    } catch (e) { toast({ title: 'Hata', description: e.response?.data?.detail || 'Başarısız' }); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Silinsin mi?')) return;
    try { await client.delete(`/admin/sliders/${id}`); toast({ title: 'Silindi' }); load(); }
    catch (e) { toast({ title: 'Hata', description: e.message }); }
  };
  const toggle = async (s) => {
    try { await client.patch(`/admin/sliders/${s.id}`, { is_active: !s.is_active }); load(); }
    catch {}
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(56,189,248,0.15)', color: 'var(--v2-info, #38bdf8)' }}><GalleryHorizontal size={18} /></div>
        <div>
          <h1 className="text-xl font-bold">Ana Sayfa Görselleri</h1>
          <p className="text-xs text-slate-400">Slider bannerları ve kategori kartları</p>
        </div>
      </div>

      <div className="admin-filter-row mb-4">
        <div className="admin-filter-scroll">
          <button
            type="button"
            onClick={() => setTab('banners')}
            className={`admin-tab ${tab === 'banners' ? 'active' : ''}`}
          >
            <GalleryHorizontal size={13} /> Bannerlar
          </button>
          <button
            type="button"
            onClick={() => setTab('categories')}
            className={`admin-tab ${tab === 'categories' ? 'active' : ''}`}
          >
            <LayoutGrid size={13} /> Kategori Kartları
          </button>
          <button
            type="button"
            onClick={() => setTab('faq')}
            className={`admin-tab ${tab === 'faq' ? 'active' : ''}`}
          >
            <HelpCircle size={13} /> Sıkça Sorulan Sorular
          </button>
        </div>
      </div>

      {tab === 'faq' ? <HomeFaqEditor /> : tab === 'categories' ? <HomeCategoryCards /> : (
      <>
      <div className="flex items-center gap-3 mb-4">
        <div>
          <h2 className="font-bold">Slider Bannerları</h2>
          <p className="text-xs text-slate-400">
            Ana slider ve yan kampanya bannerları · {SLIDE_SIZE_HINT}
          </p>
        </div>
        <button onClick={openNew} className="admin-btn-primary ml-auto flex items-center gap-2"><Plus size={14} /> Yeni Banner</button>
      </div>

      {loading ? (
        <div className="admin-card p-10 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
      ) : items.length === 0 ? (
        <div className="admin-card p-10 text-center text-slate-500">Henüz banner eklenmemiş</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <div key={s.id} className="admin-card overflow-hidden">
              <div className="relative bg-slate-800" style={{ aspectRatio: SLIDE_RATIO }}>
                {s.image ? <img src={resolveApiAssetUrl(s.image)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <ImgIcon size={40} className="absolute inset-0 m-auto text-slate-600" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <div className="text-2xl font-black text-yellow-400">{s.title} <span className="text-white">{s.subtitle}</span></div>
                  <div className="text-xl font-black text-yellow-400">{s.highlight}</div>
                </div>
                <div className="absolute top-3 right-3 flex gap-1">
                  <span className="badge-pill badge-blue">{placementLabels[s.placement || 'main']}</span>
                  <span className={`badge-pill ${s.is_active ? 'badge-green' : 'badge-gray'}`}>{s.is_active ? 'Aktif' : 'Pasif'}</span>
                  <span className="badge-pill badge-gray">#{s.order}</span>
                </div>
              </div>
              <div className="p-4 space-y-2">
                <p className="text-xs text-slate-400 line-clamp-2">{s.description}</p>
                <div className="flex gap-2">
                  <button onClick={() => openEdit(s)} className="admin-btn-primary flex-1 py-2 text-xs flex items-center justify-center gap-1"><Edit3 size={12} /> Düzenle</button>
                  <button onClick={() => toggle(s)} className="icon-btn view">{s.is_active ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                  <button onClick={() => remove(s.id)} className="icon-btn ban"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <h3 className="font-bold">{modal.mode === 'new' ? 'Yeni Banner' : 'Banner Düzenle'}</h3>
              <button onClick={() => setModal(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <F l="Banner Konumu">
                <select value={form.placement} onChange={(e) => setForm({ ...form, placement: e.target.value })} className="admin-input">
                  <option value="main">Ana Sayfa — Ana Slider</option>
                  <option value="side">Ana Sayfa — Sağdaki Yan Banner</option>
                  <option value="slot">Slot Sayfası — Üst Slider</option>
                  <option value="live-casino">Canlı Casino — Üst Slider</option>
                </select>
              </F>
              <F l="Sıra"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) })} className="admin-input" /></F>
              {form.placement === 'side' && (
                <div className="col-span-2 rounded-lg border border-cyan-400/15 bg-cyan-400/5 px-3 py-2 text-xs text-slate-400">
                  Yan bannerdaki başlık üst etiketi, alt başlık küçük metni, vurgu ise büyük kampanya metnini oluşturur.
                </div>
              )}
              {(form.placement === 'slot' || form.placement === 'live-casino') && (
                <div className="col-span-2 rounded-lg border border-cyan-400/15 bg-cyan-400/5 px-3 py-2 text-xs text-slate-400">
                  Bu banner {form.placement === 'slot' ? 'Slot Oyunları' : 'Canlı Casino'} sayfasının
                  en üstünde, oyun listesinin hemen üzerinde çıkar. Aynı konuma birden çok banner
                  eklenirse sıraya göre otomatik döner; hiç banner yoksa sayfada slider çizilmez.
                </div>
              )}
              <F l="Başlık (ör: 100%)"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="admin-input" /></F>
              <F l="Alt Başlık (ör: HOŞ GELDİN)"><input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="admin-input" /></F>
              <F l="Vurgu (ör: BONUSU)"><input value={form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.value })} className="admin-input" /></F>
              <F l="Açıklama" full><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="admin-input" style={{ minHeight: 60, padding: 12 }} /></F>
              <F l="Buton Metni"><input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="admin-input" /></F>
              <F l="Buton Linki (ör: /promosyonlar)"><input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} className="admin-input" /></F>
              <F l="Banner Görseli" full>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    className="admin-input min-w-0 flex-1"
                    placeholder="Görsel URL'si girin"
                  />
                  <label className={`admin-btn-secondary flex shrink-0 cursor-pointer items-center justify-center gap-2 ${uploadingImage ? 'pointer-events-none opacity-60' : ''}`}>
                    {uploadingImage ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploadingImage ? 'Yükleniyor...' : 'Bilgisayardan Yükle'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                      className="sr-only"
                      disabled={uploadingImage}
                      onChange={uploadImage}
                    />
                  </label>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  {SLIDE_SIZE_HINT} · URL kullanın veya JPG, PNG, GIF, WEBP, SVG dosyası yükleyin · en fazla {UPLOAD_MAX_LABEL}
                </p>
              </F>
              {form.image && (
                <div className="col-span-2">
                  {/* Önizleme sitedeki slider oranıyla birebir aynıdır. */}
                  <div className="rounded-lg overflow-hidden bg-slate-800" style={{ aspectRatio: SLIDE_RATIO }}>
                    <img
                      src={resolveApiAssetUrl(form.image)}
                      alt=""
                      className="w-full h-full object-cover"
                      onLoad={(event) => setImageRatio(
                        event.currentTarget.naturalWidth / (event.currentTarget.naturalHeight || 1),
                      )}
                      onError={() => setImageRatio(null)}
                    />
                  </div>
                  {ratioWarning && (
                    <p className="mt-1 text-[11px] text-amber-300">{ratioWarning}</p>
                  )}
                </div>
              )}
              <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" className="accent-cyan-500" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Yayında</label>
              <button disabled={busy || uploadingImage} onClick={save} className="admin-btn-primary col-span-2 py-3">{busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Kaydet</button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

const F = ({ l, children, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-xs text-slate-400 block mb-1">{l}</label>
    {children}
  </div>
);

export default Sliders;
